import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  TrendingUp,
  Calendar,
  Shield,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { useGetEventDetailQuery } from './eventsApi';
import { usePageHeaderContext } from '../../../context/PageHeaderContext';
import type { EventDetail, EventParticipant } from './model/event';
import { UniversalModuleView } from '../../../ui-components/listing/UniversalModuleView';
import type { ColDef } from 'ag-grid-community';
import Dropdown from '../../../ui-components/Dropdown';
import StatusTag from '../../../ui-components/StatusTag';
import ClickableImagePreview from '../../../ui-components/ClickableImagePreview';

const EventDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'info' | 'participants'>('info');
  const { setTitle, setSubtitle, setBreadcrumbs, setActions } =
    usePageHeaderContext();

  const {
    data: response,
    isLoading,
    isError,
  } = useGetEventDetailQuery(id || '');

  const eventDetail = response?.data as EventDetail;

  const images =
    eventDetail?.eventInformation?.eventImages?.map((img) => img.imageUrl) ||
    [];

  const [currentImage, setCurrentImage] = useState(0);
  const nextImage = () => setCurrentImage((prev) => (prev + 1) % images.length);
  const prevImage = () =>
    setCurrentImage((prev) => (prev - 1 + images.length) % images.length);

  useEffect(() => {
    setTitle('Event Details');
    setSubtitle('View event information and RSVP summary (read-only)');
    setActions(
      <button
        onClick={() => navigate('/events')}
        className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-neutral-700 bg-white border border-neutral-200 rounded-lg hover:bg-neutral-50 transition-colors shadow-sm"
      >
        <ArrowLeft className="w-4 h-4" /> Back to Events
      </button>
    );

    const eventName = eventDetail?.eventInformation?.eventName || 'Loading...';
    setBreadcrumbs([
      { label: 'Event Management', to: '/events' },
      { label: eventName, current: true },
    ]);

    return () => {
      setTitle(undefined);
      setSubtitle(undefined);
      setActions(undefined);
      setBreadcrumbs(undefined);
    };
  }, [
    setTitle,
    setSubtitle,
    setActions,
    setBreadcrumbs,
    navigate,
    eventDetail,
  ]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-500" />
      </div>
    );
  }

  if (isError || !eventDetail) {
    return (
      <div className="p-8 text-center">
        <h2 className="text-xl font-semibold text-neutral-800 dark:text-neutral-200">
          Event not found
        </h2>
        <button
          onClick={() => navigate('/events')}
          className="mt-4 text-brand-600 hover:underline inline-flex items-center gap-2"
        >
          <ArrowLeft className="w-4 h-4" /> Back to Events
        </button>
      </div>
    );
  }

  const stats = [
    {
      id: 'total-invited',
      label: 'Total Invited',
      value: eventDetail.summary.totalInvited,
      icon: TrendingUp,
    },
    {
      id: 'going',
      label: 'Going',
      value: eventDetail.summary.going,
      icon: TrendingUp,
    },
    {
      id: 'maybe',
      label: 'Maybe',
      value: eventDetail.summary.maybe,
      icon: TrendingUp,
    },
  ];

  const info = eventDetail.eventInformation;

  return (
    <div className="p-6 mx-auto">
      <h3 className="text-base font-bold text-neutral-900 dark:text-white mb-4">
        Summary
      </h3>
      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
        {stats.map((stat) => (
          <div
            key={stat.id}
            className="bg-white dark:bg-neutral-900 p-6 rounded-xl border border-neutral-200 dark:border-neutral-800 shadow-sm flex items-center justify-between group"
          >
            <div>
              <span className="text-2xl font-bold text-neutral-900 dark:text-white block mb-1">
                {stat.value}
              </span>
              <p className="text-xs font-medium text-neutral-500 dark:text-neutral-400">
                {stat.label}
              </p>
            </div>
            <div className="p-2 bg-brand-50 dark:bg-brand-900/20 rounded-lg text-brand-600 dark:text-brand-400">
              <stat.icon className="w-5 h-5" />
            </div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="bg-neutral-100/50 dark:bg-neutral-800/30 p-1 rounded-xl flex gap-1 mb-6">
        {[
          { id: 'info', label: 'Event Information' },
          { id: 'participants', label: 'Participants' },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id as any)}
            className={`flex-1 py-1.5 px-4 rounded-lg text-sm font-medium transition-all duration-200 ${
              activeTab === tab.id
                ? 'bg-white dark:bg-neutral-900 text-brand-600 shadow-sm ring-1 ring-black/5'
                : 'text-neutral-500 hover:text-neutral-700 dark:text-neutral-400 hover:dark:text-neutral-200'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="bg-white dark:bg-neutral-900 rounded-xl border border-neutral-200 dark:border-neutral-800 shadow-sm overflow-hidden">
        {activeTab === 'info' && (
          <div className="p-8">
            <div className="flex items-center gap-4 mb-8">
              <div className="w-10 h-10 bg-brand-50 dark:bg-brand-900/20 rounded-xl flex items-center justify-center text-brand-600 border border-brand-100 dark:border-brand-900/30">
                <Calendar className="w-5 h-5" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-neutral-900 dark:text-white">
                  Event Information
                </h3>
                <p className="text-sm text-neutral-500 dark:text-neutral-400">
                  Complete details about this event
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
              <div className="space-y-1.5">
                <span className="text-[10px] font-bold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider block">
                  Event ID
                </span>
                <div className="px-3 py-2 bg-neutral-50 dark:bg-neutral-800/50 border border-neutral-200 dark:border-neutral-700 rounded-md text-sm text-neutral-700 dark:text-neutral-300">
                  {info.eventId}
                </div>
              </div>
              <div className="space-y-1.5">
                <span className="text-[10px] font-bold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider block">
                  Event Name
                </span>
                <div className="px-3 py-2 bg-neutral-50 dark:bg-neutral-800/50 border border-neutral-200 dark:border-neutral-700 rounded-md text-sm text-neutral-700 dark:text-neutral-300">
                  {info.eventName}
                </div>
              </div>
              <div className="md:col-span-2 space-y-1.5">
                <span className="text-[10px] font-bold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider block">
                  Description
                </span>
                <div className="px-3 py-2 bg-neutral-50 dark:bg-neutral-800/50 border border-neutral-200 dark:border-neutral-700 rounded-md text-sm text-neutral-700 dark:text-neutral-300 min-h-[60px]">
                  {info.description || 'No description provided'}
                </div>
              </div>
              <div className="space-y-1.5">
                <span className="text-[10px] font-bold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider block">
                  Host Name
                </span>
                <div className="px-3 py-2 bg-neutral-50 dark:bg-neutral-800/50 border border-neutral-200 dark:border-neutral-700 rounded-md text-sm text-neutral-700 dark:text-neutral-300">
                  {info.hostName}
                </div>
              </div>
              <div className="space-y-1.5">
                <span className="text-[10px] font-bold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider block">
                  Location
                </span>
                <div className="px-3 py-2 bg-neutral-50 dark:bg-neutral-800/50 border border-neutral-200 dark:border-neutral-700 rounded-md text-sm text-neutral-700 dark:text-neutral-300">
                  {info.location || '—'}
                </div>
              </div>
              <div className="space-y-1.5">
                <span className="text-[10px] font-bold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider block">
                  Start Date & Time
                </span>
                <div className="px-3 py-2 bg-neutral-50 dark:bg-neutral-800/50 border border-neutral-200 dark:border-neutral-700 rounded-md text-sm text-neutral-700 dark:text-neutral-300">
                  {info.startDateTime}
                </div>
              </div>
              <div className="space-y-1.5">
                <span className="text-[10px] font-bold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider block">
                  Expiry Date & Time
                </span>
                <div className="px-3 py-2 bg-neutral-50 dark:bg-neutral-800/50 border border-neutral-200 dark:border-neutral-700 rounded-md text-sm text-neutral-700 dark:text-neutral-300">
                  {info.expiryDateTime}
                </div>
              </div>
              <div className="space-y-1.5">
                <span className="text-[10px] font-bold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider block">
                  Status
                </span>
                <div className="flex items-center">
                  <StatusTag value={info.status} />
                </div>
              </div>
              <div className="space-y-1.5">
                <span className="text-[10px] font-bold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider block">
                  Created Date
                </span>
                <div className="px-3 py-2 bg-neutral-50 dark:bg-neutral-800/50 border border-neutral-200 dark:border-neutral-700 rounded-md text-sm text-neutral-700 dark:text-neutral-300">
                  {info.createdDate}
                </div>
              </div>
              <div className="space-y-1.5">
                <span className="text-[10px] font-bold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider block">
                  Cover Image
                </span>

                {images.length === 0 ? (
                  <div className="flex justify-left rounded-md overflow-hidden">
                    <ClickableImagePreview
                      src="./fadeout-placeholder.svg"
                      alt="Placeholder Image"
                      className="w-full max-w-2xl h-48 sm:h-64 rounded-md object-contain"
                    />
                  </div>
                ) : images.length === 1 ? (
                  <div className="flex justify-left rounded-md overflow-hidden">
                    <ClickableImagePreview
                      src={images[0]}
                      alt="Event Image"
                      className="w-full max-w-2xl h-48 sm:h-64 rounded-md object-contain"
                    />
                  </div>
                ) : (
                  <div className="flex flex-col items-start space-y-3">
                    <div className="relative inline-flex rounded-md overflow-hidden bg-neutral-100 dark:bg-neutral-800/50">
                      <ClickableImagePreview
                        src={images[currentImage]}
                        srcs={images}
                        currentIndex={currentImage}
                        alt={`Event Image ${currentImage + 1}`}
                        className="h-48 sm:h-64 w-auto rounded-md object-contain"
                      />

                      <button
                        type="button"
                        onClick={prevImage}
                        className="absolute left-2 top-1/2 -translate-y-1/2 h-8 w-8 sm:h-10 sm:w-10 flex items-center justify-center rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors z-10"
                      >
                        <ChevronLeft className="w-5 h-5 sm:w-6 sm:h-6" />
                      </button>

                      <button
                        type="button"
                        onClick={nextImage}
                        className="absolute right-2 top-1/2 -translate-y-1/2 h-8 w-8 sm:h-10 sm:w-10 flex items-center justify-center rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors z-10"
                      >
                        <ChevronRight className="w-5 h-5 sm:w-6 sm:h-6" />
                      </button>

                      <div className="absolute bottom-2 right-2 rounded bg-black/60 px-2 py-1 text-xs text-white z-10">
                        {currentImage + 1} / {images.length}
                      </div>
                    </div>

                    <div className="flex justify-center gap-2 w-full max-w-[20rem]">
                      {images.map((_, index) => (
                        <button
                          key={index}
                          onClick={() => setCurrentImage(index)}
                          className={`h-2 w-2 sm:h-2.5 sm:w-2.5 rounded-full transition-colors ${
                            currentImage === index
                              ? 'bg-brand-600 dark:bg-brand-500'
                              : 'bg-neutral-300 dark:bg-neutral-600'
                          }`}
                          aria-label={`Go to slide ${index + 1}`}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="mt-8 p-4 bg-neutral-50 dark:bg-neutral-800/50 border border-neutral-200 dark:border-neutral-700 rounded-xl flex gap-3 text-neutral-600 dark:text-neutral-400">
              <Shield className="w-5 h-5 shrink-0 opacity-50" />
              <p className="text-sm">
                <span className="font-bold">Note:</span> This is a read-only
                view. Event details cannot be modified by administrators.
              </p>
            </div>
          </div>
        )}

        {activeTab === 'participants' && (
          <div className="p-4">
            <ParticipantsList eventId={id || ''} />
          </div>
        )}
      </div>
    </div>
  );
};

const ParticipantsList: React.FC<{ eventId: string }> = ({ eventId }) => {
  const [filterStatus, setFilterStatus] = useState<string>('');
  const extraPayload = React.useMemo(
    () => ({
      event_id: eventId,
      rsvpStatus: filterStatus === 'ALL' ? '' : filterStatus,
    }),
    [eventId, filterStatus]
  );

  const renderExtraActions = React.useCallback(
    () => (
      <div className="flex items-center gap-2">
        <div className="w-40 mr-1">
          <Dropdown
            id="status-filter"
            value={filterStatus}
            setValue={(val) => setFilterStatus(String(val))}
            options={[
              { label: 'All Status', value: 'ALL' },
              { label: 'Going', value: 'going' },
              { label: 'Maybe', value: 'maybe' },
              { label: 'Not Going', value: 'notgoing' },
            ]}
            placeholder="All Status"
            appendTo="body"
          />
        </div>
      </div>
    ),
    [filterStatus]
  );

  const columns = React.useMemo<ColDef<EventParticipant>[]>(
    () => [
      {
        headerName: 'NAME',
        field: 'name',
        minWidth: 150,
        cellClass: 'font-medium text-neutral-900 dark:text-white',
      },
      {
        headerName: 'EMAIL',
        field: 'email',
        minWidth: 200,
      },
      {
        headerName: 'RSVP STATUS',
        field: 'rsvpStatus',
        minWidth: 140,
        cellRenderer: (params: any) => {
          return <StatusTag value={params.value} />;
        },
      },
      {
        headerName: 'JOINED DATE',
        field: 'joinedDate',
        minWidth: 150,
      },
      {
        headerName: 'ROLE',
        field: 'role',
        minWidth: 130,
      },
    ],
    []
  );

  return (
    <UniversalModuleView<EventParticipant>
      moduleKey={`event-participants-${eventId}`}
      title="Event Participants"
      apiEndpoint="api/v1/event/participants"
      listMethod="GET"
      columnsConfig={columns}
      rowKey={(row, index) => `${row.email}-${index}`}
      searchPlaceholder="Search by name, email, or role..."
      extraPayload={extraPayload}
      extraActions={renderExtraActions}
      hideFilterButton={true}
    />
  );
};

export default EventDetailPage;
